import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Paper,
  SelectChangeEvent,
  Grid,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  PatientWeightState,
  NumberOfUnitsState,
  DrugAmountState,
  DrugStrengthState,
  DrugMgPerUnit,
  CalculationType,
  CalculationUnit,
  CalculatedValues,
} from '../../../types/DrugDosageCalculator.type';
import {
  getWeightInitialState,
  numberOfUnitsInitialState,
  drugAmountInitialState,
  drugStrengthInitialState,
} from '../utils/initialStates';
import {
  calculateNumberOfUnitsResult,
  calculateAmountResult,
  calculateStrengthResult,
  convertKgToLbs,
  convertLbsToKg,
} from '../utils/calculatorUtils';

interface SingleCalculatorFormProps {
  calculationType: CalculationType;
  initialWeightKg?: string;
  weightDisabled?: boolean;
  onCalculation?: (
    calculationType: CalculationType,
    values: CalculatedValues<CalculationUnit>
  ) => void;
  onBack: () => void;
}

const calculationTitles: Record<CalculationType, string> = {
  numberOfUnits: 'Calculate Number of Units',
  drugAmount: 'Calculate Amount of Drug Substance',
  drugStrength: 'Calculate Required Strength',
};

const calculationDescriptions: Record<CalculationType, string> = {
  numberOfUnits:
    'Enter the drug strength and prescribed dose to calculate how many units (ml, tablets) to administer.',
  drugAmount:
    'Enter the drug strength and number of units to calculate the mg/kg dose.',
  drugStrength:
    'Enter the number of units and desired dose to calculate the required drug strength.',
};

// Unit options for mg per unit select
const mgPerUnitOptions = [
  { value: 1, label: 'mg/ml' },
  { value: 2, label: 'mg/tablet' },
  { value: 3, label: 'mg/capsule' },
];

// Unit options for strength calculation
const strengthUnitOptions = [
  { value: 1, label: 'ml' },
  { value: 2, label: 'tablet(s)' },
  { value: 3, label: 'capsule(s)' },
];

export const SingleCalculatorForm: React.FC<SingleCalculatorFormProps> = ({
  calculationType,
  initialWeightKg = '',
  weightDisabled = false,
  onCalculation,
  onBack,
}) => {
  // Weight states
  const [weightKg, setWeightKg] = useState<PatientWeightState>(
    getWeightInitialState(initialWeightKg)
  );
  const [weightLbs, setWeightLbs] = useState<PatientWeightState>(
    getWeightInitialState(
      initialWeightKg !== '' ? convertKgToLbs(initialWeightKg) : ''
    )
  );

  // Calculator states
  const [numberOfUnitsState, setNumberOfUnitsState] =
    useState<NumberOfUnitsState>(numberOfUnitsInitialState());
  const [drugAmountState, setDrugAmountState] = useState<DrugAmountState>(
    drugAmountInitialState()
  );
  const [drugStrengthState, setDrugStrengthState] = useState<DrugStrengthState>(
    drugStrengthInitialState()
  );

  // Weight handlers
  const handleKgChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setWeightKg((prev) => ({ ...prev, value, error: false }));
      setWeightLbs((prev) => ({
        ...prev,
        value: convertKgToLbs(value),
        error: false,
      }));
    },
    []
  );

  const handleLbsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setWeightLbs((prev) => ({ ...prev, value, error: false }));
      setWeightKg((prev) => ({
        ...prev,
        value: convertLbsToKg(value),
        error: false,
      }));
    },
    []
  );

  // Helper to check if a value is valid (not empty, not zero)
  const isValidValue = (value: string): boolean => {
    const num = Number(value);
    return value !== '' && !isNaN(num) && num > 0;
  };

  // Get missing parameters for each calculation type
  const getMissingParams = useMemo((): string[] => {
    const missing: string[] = [];

    if (!isValidValue(weightKg.value)) {
      missing.push('Patient Weight');
    }

    switch (calculationType) {
      case 'numberOfUnits':
        if (!isValidValue(numberOfUnitsState.strength.value)) {
          missing.push('Drug Strength');
        }
        if (!isValidValue(numberOfUnitsState.dose.value)) {
          missing.push('Prescribed Dose');
        }
        break;
      case 'drugAmount':
        if (!isValidValue(drugAmountState.strength.value)) {
          missing.push('Drug Strength');
        }
        if (!isValidValue(drugAmountState.numberOfUnits.value)) {
          missing.push('Number of Units');
        }
        break;
      case 'drugStrength':
        if (!isValidValue(drugStrengthState.numberOfUnits.value)) {
          missing.push('Number of Units');
        }
        if (!isValidValue(drugStrengthState.dose.value)) {
          missing.push('Desired Dose');
        }
        break;
    }

    return missing;
  }, [
    weightKg.value,
    calculationType,
    numberOfUnitsState.strength.value,
    numberOfUnitsState.dose.value,
    drugAmountState.strength.value,
    drugAmountState.numberOfUnits.value,
    drugStrengthState.numberOfUnits.value,
    drugStrengthState.dose.value,
  ]);

  // Live calculation result - DO NOT call onCalculation here to avoid infinite loops
  const liveResult = useMemo((): string => {
    if (getMissingParams.length > 0) {
      return '';
    }

    const weight = Number(weightKg.value);

    switch (calculationType) {
      case 'numberOfUnits': {
        const result = calculateNumberOfUnitsResult(
          weight,
          Number(numberOfUnitsState.strength.value),
          Number(numberOfUnitsState.dose.value)
        );
        return result;
      }
      case 'drugAmount': {
        const result = calculateAmountResult(
          weight,
          Number(drugAmountState.strength.value),
          Number(drugAmountState.numberOfUnits.value)
        );
        return result;
      }
      case 'drugStrength': {
        const result = calculateStrengthResult(
          weight,
          Number(drugStrengthState.numberOfUnits.value),
          Number(drugStrengthState.dose.value)
        );
        return result;
      }
    }
  }, [
    getMissingParams,
    weightKg.value,
    calculationType,
    numberOfUnitsState.strength.value,
    numberOfUnitsState.dose.value,
    drugAmountState.strength.value,
    drugAmountState.numberOfUnits.value,
    drugStrengthState.numberOfUnits.value,
    drugStrengthState.dose.value,
  ]);

  // Use ref to track if we've already notified about this result
  const lastNotifiedResult = useRef<string | null>(null);

  // Notify parent when a valid calculation is performed (only once per result)
  useEffect(() => {
    if (
      liveResult &&
      liveResult !== lastNotifiedResult.current &&
      onCalculation
    ) {
      lastNotifiedResult.current = liveResult;

      switch (calculationType) {
        case 'numberOfUnits':
          onCalculation('numberOfUnits', {
            strength: numberOfUnitsState.strength.value,
            dose: numberOfUnitsState.dose.value,
            result: liveResult,
            unit: numberOfUnitsState.mgPerUnit.value,
          });
          break;
        case 'drugAmount':
          onCalculation('drugAmount', {
            strength: drugAmountState.strength.value,
            dose: drugAmountState.numberOfUnits.value,
            result: liveResult,
            unit: drugAmountState.mgPerUnit.value,
          });
          break;
        case 'drugStrength':
          onCalculation('drugStrength', {
            strength: drugStrengthState.numberOfUnits.value,
            dose: drugStrengthState.dose.value,
            result: liveResult,
            unit: drugStrengthState.unit.value,
          });
          break;
      }
    }
  }, [
    liveResult,
    calculationType,
    numberOfUnitsState.strength.value,
    numberOfUnitsState.dose.value,
    numberOfUnitsState.mgPerUnit.value,
    drugAmountState.strength.value,
    drugAmountState.numberOfUnits.value,
    drugAmountState.mgPerUnit.value,
    drugStrengthState.numberOfUnits.value,
    drugStrengthState.dose.value,
    drugStrengthState.unit.value,
    onCalculation,
  ]);

  const getResultLabel = () => {
    switch (calculationType) {
      case 'numberOfUnits': {
        const unit = mgPerUnitOptions.find(
          (o) => o.value === numberOfUnitsState.mgPerUnit.value
        );
        return unit?.label.replace('mg/', '') || 'units';
      }
      case 'drugAmount':
        return 'mg/kg';
      case 'drugStrength': {
        const unit = strengthUnitOptions.find(
          (o) => o.value === drugStrengthState.unit.value
        );
        return `mg/${unit?.label || 'unit'}`;
      }
    }
  };

  const hasResult = liveResult !== '';

  // Render the input fields for the current calculation type
  const renderInputFields = () => {
    switch (calculationType) {
      case 'numberOfUnits':
        return (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Drug Strength"
                value={numberOfUnitsState.strength.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNumberOfUnitsState((prev) => ({
                    ...prev,
                    strength: { ...prev.strength, value: e.target.value },
                  }))
                }
                type="number"
                size="small"
                sx={{ width: 120 }}
                inputProps={{ min: 0, step: 'any' }}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={numberOfUnitsState.mgPerUnit.value}
                  label="Unit"
                  onChange={(e: SelectChangeEvent<number>) =>
                    setNumberOfUnitsState((prev) => ({
                      ...prev,
                      mgPerUnit: {
                        ...prev.mgPerUnit,
                        value: e.target.value as DrugMgPerUnit,
                      },
                    }))
                  }
                >
                  {mgPerUnitOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Prescribed Dose"
              value={numberOfUnitsState.dose.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNumberOfUnitsState((prev) => ({
                  ...prev,
                  dose: { ...prev.dose, value: e.target.value },
                }))
              }
              type="number"
              size="small"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">mg/kg</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 'any' }}
            />
          </>
        );

      case 'drugAmount':
        return (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Drug Strength"
                value={drugAmountState.strength.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDrugAmountState((prev) => ({
                    ...prev,
                    strength: { ...prev.strength, value: e.target.value },
                  }))
                }
                type="number"
                size="small"
                sx={{ width: 120 }}
                inputProps={{ min: 0, step: 'any' }}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={drugAmountState.mgPerUnit.value}
                  label="Unit"
                  onChange={(e: SelectChangeEvent<number>) =>
                    setDrugAmountState((prev) => ({
                      ...prev,
                      mgPerUnit: {
                        ...prev.mgPerUnit,
                        value: e.target.value as DrugMgPerUnit,
                      },
                    }))
                  }
                >
                  {mgPerUnitOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Number of Units Given"
              value={drugAmountState.numberOfUnits.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDrugAmountState((prev) => ({
                  ...prev,
                  numberOfUnits: {
                    ...prev.numberOfUnits,
                    value: e.target.value,
                  },
                }))
              }
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 0, step: 'any' }}
            />
          </>
        );

      case 'drugStrength':
        return (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Number of Units"
                value={drugStrengthState.numberOfUnits.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDrugStrengthState((prev) => ({
                    ...prev,
                    numberOfUnits: {
                      ...prev.numberOfUnits,
                      value: e.target.value,
                    },
                  }))
                }
                type="number"
                size="small"
                sx={{ width: 120 }}
                inputProps={{ min: 0, step: 'any' }}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={drugStrengthState.unit.value}
                  label="Unit"
                  onChange={(e: SelectChangeEvent<number>) =>
                    setDrugStrengthState((prev) => ({
                      ...prev,
                      unit: { ...prev.unit, value: e.target.value as number },
                    }))
                  }
                >
                  {strengthUnitOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Desired Dose"
              value={drugStrengthState.dose.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDrugStrengthState((prev) => ({
                  ...prev,
                  dose: { ...prev.dose, value: e.target.value },
                }))
              }
              type="number"
              size="small"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">mg/kg</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 'any' }}
            />
          </>
        );
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={onBack}
        sx={{ mb: 2 }}
        size="small"
        color="primary"
      >
        Back to calculation types
      </Button>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          mb: 1,
        }}
      >
        {calculationTitles[calculationType]}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {calculationDescriptions[calculationType]}
      </Typography>

      {/* Two-column layout on sm+, single column on xs */}
      <Grid container spacing={3}>
        {/* Left column: Inputs */}
        <Grid item xs={12} sm={7}>
          {/* Patient Weight Section */}
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
          >
            Patient Weight
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              flexWrap: 'wrap',
            }}
          >
            <TextField
              label="Weight"
              value={weightKg.value}
              onChange={handleKgChange}
              disabled={weightDisabled}
              type="number"
              size="small"
              sx={{ width: 120 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">kg</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 'any' }}
            />
            <TextField
              label="Weight"
              value={weightLbs.value}
              onChange={handleLbsChange}
              disabled={weightDisabled}
              type="number"
              size="small"
              sx={{ width: 120 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">lbs</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 'any' }}
            />
          </Box>

          {/* Calculation Input Fields */}
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
          >
            Input Values
          </Typography>
          {renderInputFields()}
        </Grid>

        {/* Right column: Result */}
        <Grid item xs={12} sm={5}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              minHeight: 140,
              backgroundColor: hasResult ? 'success.main' : 'grey.100',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: hasResult ? 'center' : 'flex-start',
              textAlign: hasResult ? 'center' : 'left',
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: hasResult ? 'common.white' : 'text.secondary' }}
            >
              Result
            </Typography>
            {hasResult ? (
              <Typography
                variant="h3"
                sx={{ fontWeight: 'bold', color: 'common.white' }}
              >
                {liveResult}{' '}
                <Typography
                  component="span"
                  variant="h5"
                  sx={{ color: 'common.white' }}
                >
                  {getResultLabel()}
                </Typography>
              </Typography>
            ) : (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mb: 1 }}
                >
                  Enter the following to calculate:
                </Typography>
                {getMissingParams.map((param) => (
                  <Typography
                    key={param}
                    variant="body2"
                    sx={{ color: 'warning.dark', fontWeight: 500 }}
                  >
                    • {param}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SingleCalculatorForm;
